import { dayjs } from "@carrotfertility/carotene-core"
import {
  CustomModule,
  CustomModuleCondition,
  fieldName,
  isBoolean,
  isDate,
  isHeight,
  isMultiSelect,
  toHealthieAnswer
} from "./FormField"

/**
 * Conditional-display evaluation for Healthie "conditional form questions": a
 * question can be shown only when a prior question's answer satisfies a
 * condition. See IntakeForm (renders visible modules) and answers.ts (submits
 * only visible answers) — both derive visibility from `computeVisibleIds` so the
 * UI and the submitted payload can never disagree.
 *
 * Two rules make chained conditions correct:
 *   1. A condition passes only when the controlling question actually has an
 *      answer (an unanswered/empty controlling question never reveals its
 *      dependents — this also avoids negative operators like "not equal"
 *      spuriously matching an empty value).
 *   2. A dependent is visible only when its controlling question is ITSELF
 *      visible, so a stale answer left behind on a now-hidden parent can't
 *      reveal a grandchild.
 */

// The 10 operators Healthie exposes, collapsed to the semantics we implement.
type OperatorKind = "eq" | "neq" | "lt" | "gt" | "lte" | "gte" | "includes" | "excludes" | "contains" | "notContains"

// Healthie's `filter_type` tokens are plain lowercase words. Only "includes" is
// CONFIRMED from staging data so far; the other operators' tokens are still assumed
// (these alias sets cover plausible encodings). Tighten each set as its token is
// observed on a real form. An unrecognized token fails closed (hides the question); see
// evaluateCondition.
const OPERATOR_ALIASES: Record<OperatorKind, ReadonlySet<string>> = {
  eq: new Set(["==", "=", "eq", "equal", "equals", "is_equal_to"]),
  neq: new Set(["!=", "<>", "neq", "not_equal", "not_equals", "is_not_equal_to"]),
  lt: new Set(["<", "lt", "less_than", "is_less_than"]),
  gt: new Set([">", "gt", "greater_than", "is_greater_than"]),
  lte: new Set(["<=", "lte", "less_than_or_equal_to", "is_less_than_or_equal_to"]),
  gte: new Set([">=", "gte", "greater_than_or_equal_to", "is_greater_than_or_equal_to"]),
  includes: new Set(["includes"]), // confirmed
  excludes: new Set(["does_not_include", "not_include", "excludes", "exclude"]),
  contains: new Set(["contains", "contain"]),
  notContains: new Set(["does_not_contain", "not_contain", "does_not_contains"])
}

function classifyOperator(raw: string): OperatorKind | null {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  for (const kind of Object.keys(OPERATOR_ALIASES) as OperatorKind[]) {
    if (OPERATOR_ALIASES[kind].has(normalized)) return kind
  }
  return null
}

// The controlling question's current value as a list of canonical answer tokens
// (empty when unanswered). Multi-select yields every checked option; scalar
// controls yield a single token; an unchecked boolean and a blank/invalid value
// yield none, so they read as "no answer" (rule 1 above).
function answerTokens(mod: CustomModule, value: unknown): string[] {
  if (isMultiSelect(mod)) {
    return Array.isArray(value) ? value.map((v) => String(v).trim()).filter(Boolean) : []
  }
  if (isBoolean(mod)) {
    // Only a checked box counts as an answer; unchecked never reveals dependents.
    return value === true ? ["true"] : []
  }
  if (isDate(mod)) {
    return dayjs.isDayjs(value) && value.isValid() ? [value.format("YYYY-MM-DD")] : []
  }
  if (isHeight(mod)) {
    // Height's form value is { feet, inches }; normalize to total inches (as submitted) so a numeric
    // condition compares against a number, not "[object Object]".
    const inches = toHealthieAnswer(mod, value)
    return inches ? [inches] : []
  }
  const str = typeof value === "string" ? value.trim() : value == null ? "" : String(value)
  return str ? [str] : []
}

/**
 * Does `condition` pass, given the controlling question `mod` and its current
 * `value`? Returns false when the controlling question is unanswered. An
 * operator we can't classify fails closed (question hidden):
 * we only surface a dependent whose condition we can actually evaluate.
 */
function evaluateCondition(condition: CustomModuleCondition, mod: CustomModule, value: unknown): boolean {
  const tokens = answerTokens(mod, value)
  if (tokens.length === 0) return false

  const target = (condition.value_to_filter ?? "").trim()
  const kind = classifyOperator(condition.filter_type ?? "")
  // Unknown/unclassifiable operator: hide the dependent question.
  if (kind === null) return false

  switch (kind) {
    case "includes":
      return tokens.includes(target)
    case "excludes":
      return !tokens.includes(target)
    case "contains":
      return tokens.some((t) => t.includes(target))
    case "notContains":
      return !tokens.some((t) => t.includes(target))
    case "eq":
      return tokens[0] === target
    case "neq":
      return tokens[0] !== target
    case "lt":
    case "gt":
    case "lte":
    case "gte": {
      const a = Number(tokens[0])
      const b = Number(target)
      if (Number.isNaN(a) || Number.isNaN(b)) return false
      if (kind === "lt") return a < b
      if (kind === "gt") return a > b
      if (kind === "lte") return a <= b
      return a >= b
    }
  }
}

/**
 * The set of module ids that should be visible for the current form `values`
 * (keyed by `fieldName(mod)`). Unconditional modules are always visible; a
 * conditional module is visible only when its controlling module is visible and
 * its condition passes. Resolution is memoized and cycle-guarded (a condition
 * cycle resolves every member to hidden rather than looping).
 */
export function computeVisibleIds(modules: CustomModule[], values: Record<string, unknown>): Set<string> {
  const byId = new Map(modules.map((m) => [m.id, m]))
  const cache = new Map<string, boolean>()
  const resolving = new Set<string>()

  function isVisible(mod: CustomModule): boolean {
    const cached = cache.get(mod.id)
    if (cached !== undefined) return cached
    // Cycle guard: re-entering a module still being resolved breaks the loop.
    if (resolving.has(mod.id)) return false
    resolving.add(mod.id)

    let result: boolean
    const condition = mod.custom_module_condition
    // No condition, or a condition with no controlling module, => always shown.
    if (!condition || !condition.conditional_custom_module_id) {
      result = true
    } else {
      const controlling = byId.get(condition.conditional_custom_module_id)
      result =
        controlling != null &&
        isVisible(controlling) &&
        evaluateCondition(condition, controlling, values[fieldName(controlling)])
    }

    resolving.delete(mod.id)
    cache.set(mod.id, result)
    return result
  }

  const visible = new Set<string>()
  for (const mod of modules) {
    if (isVisible(mod)) visible.add(mod.id)
  }
  return visible
}
